import * as React from "react";
import {createContext, ReactElement, SetStateAction, useEffect, useState} from "react";
import {Form, Formik} from "formik";
import {FormikValues} from "formik/dist/types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPencil, faX} from "@fortawesome/pro-light-svg-icons";
import {AnyObjectSchema} from "yup";
import ActionButton from "../presentational/button/ActionButton";

type EditablePropertiesProps = {
    label?: string // without a label, the item is laid out inline, e.g. in a table cell
    fields?: ReactElement<{ values: FormikValues }>
    initialValues: object
    validationSchema?: AnyObjectSchema
    handleSubmission: (
        values: FormikValues,
        setEditingActive: SetStateAction<any>
    ) => void | Promise<any>
    disabled?: boolean
}

const EditableDataListItem: React.FC<React.PropsWithChildren<EditablePropertiesProps>> = ({label, fields, initialValues, validationSchema, handleSubmission, children, disabled = false}) => {

    const [editingActive, setEditingActive] = useState(false)

    const escapeListener = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            setEditingActive(false)
        }
    }

    useEffect(() => {
        document.addEventListener("keydown", escapeListener, false);
        return () => {
            document.removeEventListener("keydown", escapeListener, false);
        };
    }, []);

    const FormikValuesContext = createContext({});

    if (editingActive && label === undefined) {
        return (
            <Formik
                initialValues={initialValues}
                onSubmit={(values) => {
                    handleSubmission(values, setEditingActive)
                }}
                validationSchema={validationSchema}
            >
                {({isSubmitting, dirty}) => (
                    <Form className="flex items-start text-sm text-gray-900">
                        <div className="grow">{fields}</div>
                        <div className="ml-2 shrink-0">
                            <ActionButton isSubmitting={isSubmitting} disabled={!dirty} label="Save"/>
                            <button type="button" disabled={isSubmitting} onClick={() => setEditingActive(false)} className="py-2 px-3 text-sm font-medium text-gray-700">
                                <FontAwesomeIcon icon={faX}/>
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        );
    }

    if (label === undefined) {
        return (
            <div
                className={`group/editable flex items-center ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
                onClick={() => {
                    if (!disabled) {
                        setEditingActive(true)
                    }
                }}
            >
                <span>{children}</span>
                {!disabled && (
                    <span className="ml-2 shrink-0 text-gray-400 invisible group-hover/editable:visible">
                        <FontAwesomeIcon icon={faPencil}/>
                    </span>
                )}
            </div>
        );
    }

    if (editingActive) {
        return (
            <Formik
                initialValues={initialValues}
                onSubmit={(values) => {
                    handleSubmission(values, setEditingActive)
                }}
                validationSchema={validationSchema}
            >
                {({isSubmitting, dirty, values}) => (
                    <Form className="group py-1 sm:grid sm:grid-cols-3 sm:gap-4 cursor-pointer sm:py-2 bg-gray-100">
                        <dt className="text-sm font-medium text-gray-500 sm:py-3">{label}</dt>
                        <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            <div className="grow">
                                <FormikValuesContext.Provider value={values}>
                                    {fields}
                                </FormikValuesContext.Provider>
                            </div>
                            <div className="ml-4 shrink-0">
                                <ActionButton isSubmitting={isSubmitting} disabled={!dirty} label="Save"/>
                                <button type="button" disabled={isSubmitting} onClick={e => setEditingActive(false)} className="py-2 px-4 text-sm font-medium text-gray-700">
                                    <FontAwesomeIcon icon={faX}/>
                                </button>
                            </div>
                        </dd>
                    </Form>
                )}
            </Formik>
        );
    }

    return (
        <div
            className={`group py-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-2 ${disabled ? 'cursor-default' : 'cursor-pointer hover:bg-gray-100'}`}
            onClick={() => {
                if (!disabled) {
                    setEditingActive(true)
                }
            }}
        >
            <dt className="text-sm font-medium text-gray-500">{label}</dt>
            <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="grow">{children}</span>
                {!disabled && (
                    <span className="mx-4 shrink-0 invisible group-hover:visible">
                        <FontAwesomeIcon icon={faPencil}/>
                    </span>
                )}
            </dd>
        </div>
    )
}

export default EditableDataListItem;
